import { useState, useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { productsApi, stockApi } from '../api/client';
import Card from '../components/Card';
import AddProductModal from '../components/AddProductModal';
import Input from '../components/Input';

// Функция форматирования количества: целые числа без десятичной части, дробные с одним знаком
const formatQty = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0';
  return num % 1 === 0 ? num.toString() : num.toFixed(1);
};

const Products = () => {
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDioptries, setSelectedDioptries] = useState([]);
  const [showDioptryFilter, setShowDioptryFilter] = useState(false);

  const { data: products, isLoading, error } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const response = await productsApi.getAll();
      return response.data;
    },
  });

  // Получаем остатки по диоптриям для всех товаров параллельно
  const stockQueries = useQueries({
    queries: (products || []).map((product) => ({
      queryKey: ['stock', product.id],
      queryFn: async () => {
        const response = await stockApi.getStock(product.id);
        return response.data;
      },
      enabled: !!product.id,
    })),
  });

  // Создаем мапу остатков по диоптриям для быстрого доступа
  const stockByProductId = useMemo(() => {
    const map = {};
    stockQueries.forEach((query, index) => {
      if (query.data && products?.[index]) {
        map[products[index].id] = query.data;
      }
    });
    return map;
  }, [stockQueries, products]);

  // Собираем все уникальные диоптрии из всех товаров
  const availableDioptries = useMemo(() => {
    const dioptriesSet = new Set();
    
    Object.values(stockByProductId).forEach((stockData) => {
      if (stockData?.stock_by_dioptry) {
        stockData.stock_by_dioptry.forEach((item) => {
          // Используем строковое представление для ключа, чтобы null, 0 и отрицательные числа обрабатывались корректно
          const key = item.dioptry === null ? 'null' : String(item.dioptry);
          dioptriesSet.add(key);
        });
      }
    });
    
    // Преобразуем обратно в значения и сортируем
    return Array.from(dioptriesSet)
      .map(key => key === 'null' ? null : parseFloat(key))
      .sort((a, b) => {
        if (a === null) return 1;
        if (b === null) return -1;
        return a - b;
      });
  }, [stockByProductId]);

  // Фильтрация товаров по поисковому запросу и диоптриям
  const filteredProducts = useMemo(() => {
    if (!products) {
      return [];
    }

    let filtered = products;

    // Фильтрация по поисковому запросу
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(product => {
        const nameMatch = product.name?.toLowerCase().includes(query);
        const itemCodeMatch = product.item_code?.toLowerCase().includes(query);
        return nameMatch || itemCodeMatch;
      });
    }

    // Фильтрация по диоптриям
    if (selectedDioptries.length > 0) {
      filtered = filtered.filter(product => {
        const stockData = stockByProductId[product.id];
        if (!stockData?.stock_by_dioptry || stockData.stock_by_dioptry.length === 0) {
          return false;
        }

        // Проверяем, есть ли у товара хотя бы одна из выбранных диоптрий с остатком > 0
        return stockData.stock_by_dioptry.some(item => {
          const itemKey = item.dioptry === null ? 'null' : String(item.dioptry);
          return selectedDioptries.includes(itemKey) && item.stock > 0;
        });
      });
    }

    return filtered;
  }, [products, searchQuery, selectedDioptries, stockByProductId]);

  // Функция для переключения диоптрии в фильтре
  const toggleDioptry = (dioptryKey) => {
    setSelectedDioptries(prev => {
      if (prev.includes(dioptryKey)) {
        return prev.filter(key => key !== dioptryKey);
      } else {
        return [...prev, dioptryKey];
      }
    });
  };

  // Функция для форматирования диоптрии для отображения
  const formatDioptryLabel = (dioptry) => {
    if (dioptry === null) return 'Без диоптрий';
    if (dioptry === 0 || dioptry === '0') return '0';
    return '-' + parseFloat(dioptry).toFixed(2);
  };

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="text-center py-8">Загрузка товаров...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="text-center py-8 text-red-600">
          Ошибка загрузки товаров
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-4 pb-24">
        <div className="mb-4">
          <div className="flex justify-between items-center mb-3">
            <h1 className="text-2xl font-bold text-gray-900">Товары</h1>
            <div className="flex items-center gap-3">
              {(searchQuery || selectedDioptries.length > 0) && filteredProducts && (
                <span className="text-sm text-gray-500">
                  Найдено: {filteredProducts.length}
                </span>
              )}
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-blue-600 text-white rounded-lg px-4 py-2 hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <span className="text-lg">+</span>
                <span>Добавить</span>
              </button>
            </div>
          </div>
          
          <div className="mb-3 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="🔍 Поиск по названию или артикулу..."
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-base"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg"
                aria-label="Очистить поиск"
              >
                ✕
              </button>
            )}
          </div>

          {/* Фильтр по диоптриям */}
          {availableDioptries.length > 0 && (
            <div className="mb-3">
              <button
                onClick={() => setShowDioptryFilter(!showDioptryFilter)}
                className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
              >
                <span>
                  🔍 Фильтр по диоптриям
                  {selectedDioptries.length > 0 && (
                    <span className="ml-2 text-blue-600">
                      ({selectedDioptries.length} выбрано)
                    </span>
                  )}
                </span>
                <span className={`transform transition-transform ${showDioptryFilter ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </button>
              
              {showDioptryFilter && (
                <div className="mt-2 p-3 border border-gray-300 rounded-lg bg-white">
                  {selectedDioptries.length > 0 && (
                    <div className="mb-2">
                      <button
                        onClick={() => setSelectedDioptries([])}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        Очистить фильтр
                      </button>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                    {availableDioptries.map((dioptry) => {
                      const dioptryKey = dioptry === null ? 'null' : String(dioptry);
                      const isSelected = selectedDioptries.includes(dioptryKey);
                      
                      return (
                        <button
                          key={dioptryKey}
                          onClick={() => toggleDioptry(dioptryKey)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            isSelected
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {formatDioptryLabel(dioptry)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {products && products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Товаров пока нет</p>
          </div>
        ) : filteredProducts && filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Товары не найдены</p>
            {(searchQuery || selectedDioptries.length > 0) && (
              <p className="text-sm text-gray-400 mt-2">
                {searchQuery && `По запросу "${searchQuery}" `}
                {selectedDioptries.length > 0 && `С выбранными диоптриями `}
                ничего не найдено
              </p>
            )}
          </div>
        ) : (
          <div>
            {filteredProducts?.map((product) => (
              <Card
                key={product.id}
                onClick={() => navigate(`/products/${product.id}`)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {product.name}
                    </h3>
                    {product.item_code && (
                      <p className="text-sm font-bold text-blue-600 mb-2 font-mono">
                        Артикул: {product.item_code}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      {(product.diameter !== null && product.diameter !== undefined) && (
                        <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                          Диаметр: {parseFloat(product.diameter).toFixed(1)}
                        </span>
                      )}
                    </div>
                    {/* Диоптрии в наличии */}
                    {stockByProductId[product.id]?.stock_by_dioptry && 
                     stockByProductId[product.id].stock_by_dioptry.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs text-gray-500 mb-1">Диоптрии в наличии:</p>
                        <div className="flex flex-wrap gap-1">
                          {stockByProductId[product.id].stock_by_dioptry.map((item, idx) => (
                            <span 
                              key={idx} 
                              className="text-xs font-medium text-gray-600 bg-blue-50 px-2 py-1 rounded"
                            >
                              {item.dioptry === null ? 'Без диоптрий' : (item.dioptry === 0 || item.dioptry === '0' ? '0' : "-" +parseFloat(item.dioptry).toFixed(2))}
                              <span className="ml-1 text-blue-600">({formatQty(item.stock)})</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-4 mb-2">
                      <p className={`text-sm font-semibold ${
                        (product.stock || 0) > 3 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        Остаток: {formatQty(product.stock || 0)}
                      </p>
                    </div>
                    <p className="text-lg font-bold text-blue-600">
                      {parseFloat(product.price).toFixed(2)} ₸
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <AddProductModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />
    </>
  );
};

export default Products;

